import React from 'react';
import { Paper, Typography, Button, Box } from '@mui/material';
import {
  SearchOff as SearchOffIcon,
  FilterAltOff as FilterAltOffIcon,
  Refresh as RefreshIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';

const NoResultsFound = ({ 
  hasActiveFilters, 
  onResetFilters, 
  onBackToSearch,
  searchQuery 
}) => {
  // 🆕 Generate structured data for empty search results
  const emptySearchSchema = {
    "@context": "https://schema.org",
    "@type": "SearchResultsPage",
    "mainEntity": {
      "@type": "ItemList",
      "name": hasActiveFilters 
        ? `Filtered search results for "${searchQuery}"` 
        : `Search results for "${searchQuery}"`,
      "numberOfItems": 0,
      "itemListElement": []
    },
    "description": hasActiveFilters 
      ? "No businesses found matching the current search filters. Try adjusting filters or search terms."
      : `No businesses found for "${searchQuery}". Try different search terms or browse categories.`
  };

  const handleResetFilters = () => {
    if (onResetFilters) {
      onResetFilters();
    }
  };

  const handleBackToSearch = () => {
    if (onBackToSearch) {
      onBackToSearch();
    }
  };

  return (
    <Paper 
      elevation={0} 
      sx={{ 
        p: 4, 
        textAlign: 'center',
        backgroundColor: 'background.default',
        borderRadius: 2,
        minHeight: '300px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2
      }}
      // 🆕 Semantic HTML and accessibility
      component="section"
      role="status" // 🆕 Indicates this is a status message
      aria-live="polite" // 🆕 Screen reader will announce this content
      aria-label={hasActiveFilters ? "No search results with current filters" : "No search results found"}
      itemScope
      itemType="https://schema.org/SearchResultsPage" // 🆕 Microdata
    >
      {/* 🆕 Hidden structured data for empty search results */}
      <script 
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(emptySearchSchema)
        }}
      />

      {/* 🆕 Hidden metadata for SEO */}
      <meta itemProp="numberOfItems" content="0" />
      <meta itemProp="description" content={hasActiveFilters 
        ? "No businesses found matching the current search filters" 
        : `No businesses found for "${searchQuery}"`} />

      {hasActiveFilters ? (
        <FilterAltOffIcon 
          sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} 
          aria-hidden="true" // 🆕 Decorative icon
        />
      ) : (
        <SearchOffIcon 
          sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} 
          aria-hidden="true" // 🆕 Decorative icon
        />
      )}

      <Typography 
        variant="h5" 
        gutterBottom
        component="h2" // 🆕 Proper heading hierarchy (assuming h1 is on parent page)
        itemProp="name" // 🆕 Microdata
        id="no-results-heading" // 🆕 For accessibility reference
      >
        {hasActiveFilters ? 'No matches found with current filters' : 'No results found'}
      </Typography>

      <Typography 
        variant="body1" 
        color="text.secondary" 
        sx={{ mb: 3 }}
        itemProp="description" // 🆕 Microdata
        aria-describedby="no-results-heading" // 🆕 Links description to heading
      >
        {hasActiveFilters 
          ? "Try adjusting your filters to see more results, or search for different terms."
          : `We couldn't find any businesses matching "${searchQuery}". Try different keywords or browse our categories.`}
      </Typography>

      {/* 🆕 Enhanced suggestions section */}
      <Box 
        sx={{ mb: 2 }}
        component="div"
        role="complementary" // 🆕 Indicates this provides additional helpful content
        aria-label="Search suggestions"
      >
        <Typography 
          variant="body2" 
          color="text.secondary"
          component="p"
        >
          <strong>Suggestions:</strong>
        </Typography>
        <Typography 
          variant="body2" 
          color="text.secondary"
          component="ul"
          sx={{ 
            listStyle: 'none', 
            padding: 0, 
            margin: '8px 0',
            textAlign: 'left',
            display: 'inline-block'
          }}
        >
          {hasActiveFilters ? (
            <>
              <li>• Remove or adjust your current filters</li>
              <li>• Try searching with broader criteria</li>
              <li>• Check your location settings</li>
            </>
          ) : (
            <>
              <li>• Check your spelling</li>
              <li>• Try more general terms</li>
              <li>• Browse by category instead</li>
              <li>• Try searching in a different location</li>
            </>
          )}
        </Typography>
      </Box>

      <Box 
        sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}
        component="nav" // 🆕 Navigation actions
        aria-label="Search result actions"
      >
        {hasActiveFilters && (
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={handleResetFilters}
            aria-label="Reset all search filters to see more results" // 🆕 Descriptive label
            // 🆕 Analytics/tracking attributes
            data-action="reset-filters"
            data-context="no-results"
          >
            Reset Filters
          </Button>
        )}
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={handleBackToSearch}
          aria-label="Go back to search page to try a new search" // 🆕 Descriptive label
          // 🆕 Analytics/tracking attributes  
          data-action="back-to-search"
          data-context="no-results"
        >
          Back to Search
        </Button>
      </Box>

      {/* 🆕 Alternative search suggestions */}
      <Box 
        sx={{ 
          mt: 3, 
          p: 2, 
          backgroundColor: 'grey.50', 
          borderRadius: 1,
          maxWidth: '400px'
        }}
        component="aside"
        aria-label="Popular search categories"
      >
        <Typography 
          variant="subtitle2" 
          gutterBottom
          component="h3" // 🆕 Proper heading hierarchy
        >
          Popular Categories
        </Typography>
        <Box 
          sx={{ 
            display: 'flex', 
            gap: 1, 
            flexWrap: 'wrap', 
            justifyContent: 'center' 
          }}
        >
          {['Restaurants', 'Dentists', 'Lawyers', 'Coffee Shops', 'Hotels'].map((category) => (
            <Button
              key={category}
              size="small"
              variant="text"
              onClick={() => {
                // 🆕 Could trigger a new search with this category
                if (onBackToSearch) {
                  onBackToSearch();
                }
              }}
              aria-label={`Search for ${category}`}
              data-action="category-search"
              data-category={category.toLowerCase()}
              sx={{ 
                minWidth: 'auto',
                textTransform: 'none',
                fontSize: '0.75rem'
              }}
            >
              {category}
            </Button>
          ))}
        </Box>
      </Box>

      {/* 🆕 Hidden content for SEO - search intent and suggestions */}
      <div style={{ display: 'none' }} aria-hidden="true">
        <span itemProp="keywords">
          no results, empty search, {searchQuery}, business directory, local businesses
        </span>
        <span itemProp="searchAction">
          Users can reset filters, try new search terms, or browse categories when no results found
        </span>
      </div>
    </Paper>
  );
};

export default NoResultsFound;